'use client'

import { useState, useEffect } from 'react'
import Link from 'next/link'
import { Trophy } from 'lucide-react'
import { FglHeader } from '@/components/layout/fgl-header'
import { FglV1_0UpdateBanner } from '@/components/fgl-v1-0-update-banner'
// r19.55.45 (Stephan-Direktive 2026-07-13): NewsImageMarquee auf /news
// verschoben — nicht mehr auf Frontseite.
// r19.45 (Stephan-Spec 2026-06-26): Race-Track-Hero pro Terrain auf Frontpage.
import { FglRaceTrack, buildWertungLink, type RaceTrackView } from '@/components/wertung/fgl-race-track'
import { useSession } from 'next-auth/react'
// r19.55.179 (Stephan-Spec 2026-08-10): LaunchCountdown-Import entfernt,
// Countdown wird auf der Landingpage nicht mehr angezeigt. State (launchAt)
// bleibt bewusst — er triggert im Backend weitere Flags (registrationEnabled).
import { useTranslation } from '@/i18n/client'
import {
  LenisProvider,
  Reveal,
  Magnetic,
  CountUp,
  LivingThermal,
  GliderBackdrop, GliderOrbit,
} from '@/components/award'

interface TickerFlight {
  flight_guid: string
  pilot_name: string
  pilot_guid: string | null
  country: string | null
  club: string | null
  terrain: string
  class_key: string
  duration_label: string
  score_km: number
  avg_speed_kmh: number | null
  flight_url: string | null
}

interface RankingRow {
  rank: number
  name: string
  slug: string | null
  points: number
  flights: number
  distanceKm: number
}

const FLAG_BASE = 'https://flagcdn.com/'

function flagImg(cc: string | null) {
  const c = (cc || '').trim().toLowerCase()
  if (!/^[a-z]{2}$/.test(c)) return null
  return (
    <img
      className="inline-block w-[18px] h-[12px] rounded-[2px] object-cover"
      src={`${FLAG_BASE}${c}.svg`}
      alt=""
      loading="lazy"
      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
    />
  )
}

function fmtKm(n: number) {
  return n.toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 })
}

export default function Home() {
  const { t } = useTranslation()
  const [ticker, setTicker] = useState<{ flatland: TickerFlight[]; slope: TickerFlight[] } | null>(null)
  const [modelStats, setModelStats] = useState({ manufacturers: 0, models: 0, polars: 0 })
  const [hotspotCount, setHotspotCount] = useState(0)
  const [platformStats, setPlatformStats] = useState<{
    totalPilots: number; totalFlights: number; totalDistanceKm: number
    thermalHotspots?: number; totalModels?: number; totalPolars?: number
    sinkPolars?: number; sink5Polars?: number; circlePolars?: number; polarContributions?: number
  } | null>(null)
  const [community, setCommunity] = useState<{
    flatlandKm: number; slopeKm: number; yearTotalKm: number
    yearTotalFlights: number; yearEarthRounds: number
    pilots: number; nations: number
    // r19.55.40 (2026-07-13, Stephan-Wunsch): Piloten-Kachel auf FGL-Homepage
    // wird von „alle aktiven Piloten" auf „einreichende Piloten pro Terrain"
    // umgestellt. Die API liefert flatlandPilots + slopePilots bereits.
    flatlandPilots?: number; slopePilots?: number
    nationList?: Array<{ iso: string; pilots: number; km?: number }>
  } | null>(null)
  const [rankings, setRankings] = useState<RankingRow[]>([])
  // r19.49 (2026-06-29): Logger-Verteilung + Workaround-Quote für Frontseite-Tile
  const [loggerDist, setLoggerDist] = useState<{
    totalRelevantFlights: number
    workaroundFlights: number
    workaroundQuote: number
    buckets: Array<{
      code: string
      manufacturer: string
      model: string
      flights: number
      requiresWorkaround: boolean
      recentFlights: number
      previousFlights: number
      trendDirection: 'up' | 'down' | 'flat'
      trendDeltaShare: number
    }>
    trend: {
      recentWorkaroundQuote: number
      previousWorkaroundQuote: number
      deltaPp: number
      direction: 'up' | 'down' | 'flat'
      recentFlights: number
      previousFlights: number
    } | null
  } | null>(null)
  const [fglWertungActive, setFglWertungActive] = useState(false)
  // 2026-05-15: Top-10-Liste auf Startseite respektiert die 3-Stufen-Sichtbarkeit.
  // Bei 'hidden' wird die namentliche Top-10-Liste komplett versteckt (Aggregate-Stats bleiben).
  const [fglWertungVisibility, setFglWertungVisibility] = useState<'hidden' | 'tier_gated' | 'public'>('hidden')
  // R101 (2026-05-31): Launch-Termin für Countdown
  const [launchAt, setLaunchAt] = useState<string | null>(null)
  const [registrationEnabled, setRegistrationEnabled] = useState<boolean>(false)

  useEffect(() => {
    fetch('/api/public/site-state').then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d?.fglWertungActive) setFglWertungActive(true)
        const v = d?.fglWertungVisibility
        if (v === 'public' || v === 'tier_gated' || v === 'hidden') setFglWertungVisibility(v)
        if (typeof d?.launchAt === 'string') setLaunchAt(d.launchAt)
        if (typeof d?.registrationEnabled === 'boolean') setRegistrationEnabled(d.registrationEnabled)
      }).catch(() => {})
    fetch('/api/models/search?manufacturers=true').then(r => r.ok ? r.json() : { data: [] })
      .then(j => {
        const data = j.data || []
        setModelStats({
          manufacturers: data.length,
          models: data.reduce((s: number, m: any) => s + (m.modelCount || 0), 0),
          polars: data.reduce((s: number, m: any) => s + (m.polarCount || 0), 0),
        })
      }).catch(() => {})
    fetch('/api/public/thermal-atlas?lat=50&lon=10&radius=9999').then(r => r.ok ? r.json() : { hotspots: [] })
      .then(j => setHotspotCount(j.hotspots?.length || j.count || 0)).catch(() => {})
    fetch('/api/rankings/ticker?year=2026&limit=50').then(r => r.ok ? r.json() : { data: null })
      .then(j => { if (j.success) setTicker(j.data) }).catch(() => {})
    fetch('/api/public/stats').then(r => r.ok ? r.json() : null)
      .then(j => { if (j?.data) setPlatformStats(j.data) }).catch(() => {})
    // r19.49: Logger-Verteilung für Transparenz-Tile
    fetch('/api/public/stats/logger-distribution').then(r => r.ok ? r.json() : null)
      .then(j => { if (j?.data) setLoggerDist(j.data) }).catch(() => {})
    fetch(`/api/rankings/community-stats?year=${new Date().getFullYear()}`)
      .then(r => r.ok ? r.json() : null)
      .then(j => { if (j?.data) setCommunity(j.data) }).catch(() => {})

    const fetchRankings = () => {
      fetch('/api/public/rankings?type=free_glide&limit=10').then(r => {
        // 2026-05-15: bei gesperrter Wertung (423) oder Login-Anforderung (401)
        // bleibt die Top-10-Liste leer — die Section auf der Startseite wird ausgeblendet.
        if (!r.ok) { setRankings([]); return null }
        return r.json()
      }).then(j => { if (j?.data?.rankings) setRankings(j.data.rankings); else setRankings([]) }).catch(() => setRankings([]))
    }
    fetchRankings()
    const id = setInterval(fetchRankings, 60_000)
    return () => clearInterval(id)
  }, [])

  const tickerAll = [...(ticker?.slope || []), ...(ticker?.flatland || [])].slice(0, 40)

  return (
    <LenisProvider>
      <div data-award="true" className="relative min-h-screen overflow-x-clip" style={{ background: 'var(--award-ink)' }}>
        <FglHeader />
        <FglV1_0UpdateBanner />

        {/* r19.55.45 (Stephan-Direktive 2026-07-13): News-Bilder-Laufband
            auf der Landing-Page entfernt. Es lebt jetzt ausschliesslich auf
            der /news-Seite. Der Grund: auf der Homepage konkurriert die
            Laufzeile mit dem Race-Track-Hero + Community-Stats + Bento-Grid
            um Aufmerksamkeit. Auf /news ist sie sinnvoll als Uebersicht.
            2026-06-14-Kommentar davor ist damit ueberholt. */}

        {/* r19.45 (Stephan-Spec 2026-06-26): Live-Race-Track-Hero — 2 Streifen,
            einer pro Terrain (Flatland + Slope sind getrennte Wertungen). Hover
            auf einen Avatar zeigt Sort-Card mit klickbaren Flug-Pillen.
            Eingeloggte sehen sich selbst markiert. */}
        <FrontpageRaceHero />

      <GliderBackdrop />

        {/* ═══ HERO ═══
            2026-06-14: pt reduziert, weil der News-Marquee oben den
            Header-Clearance liefert (vorher pt-[110px]/md:pt-[150px]). */}
        <section className="relative pt-6 pb-12 md:pt-10 md:pb-16 overflow-hidden">
          {/* Segler rechts unter der Headline — nimmt Raum, drängt sich aber nicht auf */}
          <div className="absolute right-[-6%] top-[14%] w-[46%] max-w-[720px] hidden md:block pointer-events-none opacity-90" aria-hidden>
          </div>

          <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12">
            <Reveal>
              {/* r19.55.179 (Stephan-Spec 2026-08-10): Live-Countdown zum Launch
                  komplett entfernt — Registrierung ist ohnehin offen (mit Codes
                  für Tester bis 03.06.2026). Nur noch das Eyebrow-Label bleibt. */}
              <div className="flex items-center gap-3 mb-6">
                <span className="award-eyebrow">{t('home.heroEyebrow')}</span>
                <span className="h-px w-16 bg-[var(--award-line-hi)]" />
                <span className="award-label">{t('home.heroLaunchLabel')}</span>
              </div>
            </Reveal>

            <div className="grid grid-cols-12 gap-6 md:gap-12 items-end">
              {/* Headline-Spalte — Brand + 3-zeiliger Subtitel */}
              <div className="col-span-12 md:col-span-7">
                <Reveal delay={1}>
                  <h1
                    className="award-h-mega"
                    style={{ color: 'var(--accent)', textShadow: '0 0 32px rgba(242,201,78,0.18)' }}
                  >
                    FREE GLIDE LEAGUE
                  </h1>
                </Reveal>
                <Reveal delay={2}>
                  <div
                    className="mt-5 md:mt-6 space-y-1"
                    style={{
                      fontFamily: 'var(--font-display)',
                      fontSize: 'clamp(22px, 2.6vw, 40px)',
                      lineHeight: 1.18,
                      fontWeight: 600,
                      letterSpacing: '-0.018em',
                      color: 'var(--text)',
                    }}
                  >
                    <div>{t('home.heroLine1')}</div>
                    <div>{t('home.heroLine2')}</div>
                    <div className="award-grad-text">{t('home.heroLine3')}</div>
                  </div>
                </Reveal>
              </div>

              {/* Text + CTAs — rechte Spalte */}
              <div className="col-span-12 md:col-span-5">
                <Reveal delay={2}>
                  {/* R102 (Stephan-Wunsch 2026-05-31) → R103.30 (T-Day):
                      "Offizielle FREE GLIDE LEAGUE Wertung" als grosse pulsierende
                      Seal-Pille — Original-Charakter, Kraft, Bescheidenheit zugleich.
                      Keyframe + .fgl-official-seal-Klasse liegen in globals.css. */}
                  <div
                    className="fgl-official-seal mb-5 inline-flex items-center gap-2.5 px-5 py-2.5 rounded-full text-[13px] sm:text-[14px] font-black uppercase tracking-[.18em]"
                    style={{
                      background:
                        'linear-gradient(135deg, color-mix(in oklch, var(--award-gold) 14%, transparent) 0%, color-mix(in oklch, var(--award-gold) 22%, transparent) 100%)',
                      color: 'var(--award-gold)',
                      border: '1.5px solid color-mix(in oklch, var(--award-gold) 55%, transparent)',
                      textShadow: '0 0 14px rgba(242,201,78,0.45)',
                      letterSpacing: '0.18em',
                    }}
                  >
                    <Trophy className="w-4 h-4 shrink-0" />
                    <span>{t('home.officialSealText')}</span>
                  </div>
                  <p className="award-body-lead">
                    {t('home.heroLead')}
                  </p>
                  <p className="mt-4 text-[15px] leading-relaxed" style={{ color: 'var(--award-cirrus-2)' }}>
                    <span className="text-[var(--award-gold)] font-semibold">{t('home.heroFeatureFGL')}</span>,
                    <span className="text-[var(--award-gold)] font-semibold"> {t('home.heroFeatureAI')}</span>,
                    <span className="text-[var(--award-gold)] font-semibold"> {t('home.heroFeatureThermal')}</span>,
                    <span className="text-[var(--award-gold)] font-semibold"> {t('home.heroFeaturePolar')}</span>. <br />
                    {t('home.heroQuestion')}
                  </p>
                  <p className="mt-4 text-[14px] leading-relaxed" style={{ color: 'var(--award-cirrus-2)' }}>
                    {t('home.heroOnlyData')}
                  </p>
                  <p className="mt-4 text-[14px] leading-relaxed" style={{ color: 'var(--award-cirrus-2)' }}>
                    {t('home.heroNoSubs')}
                    <span className="text-[var(--award-gold)] font-semibold"> {t('home.heroTier1')}</span>,
                    <span className="text-[var(--award-gold)] font-semibold"> {t('home.heroTier2')}</span>,
                    <span className="text-[var(--award-gold)] font-semibold"> {t('home.heroTier3')}</span>.
                  </p>
                </Reveal>

                <Reveal delay={3}>
                  <div className="mt-7 flex flex-wrap items-center gap-3">
                    {fglWertungActive && (
                      <Magnetic>
                        <Link href="/wertung" className="award-btn award-btn-primary">
                          {t('home.heroCtaRanking')}
                          <span aria-hidden>→</span>
                        </Link>
                      </Magnetic>
                    )}
                    <Magnetic>
                      <Link href="/features" className="award-btn award-btn-ghost">
                        {t('home.heroCtaFeatures')}
                      </Link>
                    </Magnetic>
                  </div>
                  {/* R102 (Stephan-Wunsch 2026-05-31): Tester-Hinweis-Zeile
                      auf der Landing-Page komplett entfernt — der Countdown oben
                      sagt schon alles, die Doppelung wirkt redundant. */}
                </Reveal>
              </div>
            </div>

            {/* Stats-Leiste unten — R56 (2026-05-16): 12 Tiles in 4x3-Grid (auf lg)
                 → Sinkpolaren + Kreispolaren als 2 separate Tiles. */}
            <Reveal delay={4} slow>
              <div className="mt-8 md:mt-10 pt-6 border-t border-[var(--award-line)] grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-x-5 gap-y-6">
                {(() => {
                  // r19.55.40 (2026-07-13, Stephan-Wunsch):
                  // 1) Piloten-Kachel „Piloten 2026" ersetzt durch 2 Kacheln
                  //    „Piloten Flatland" + „Piloten Slope" (einreichende Piloten
                  //    pro Terrain, nicht die Gesamtzahl aller aktiven Piloten).
                  // 2) Neue Logger-Kachel „Logger SM-Modellbau" mit Prozentanteil
                  //    an allen relevanten Fluegen. Zeigt was fuer die Wertung
                  //    einreichebar bleibt (nach RCE-CSV-Umstellung).
                  // 3) „IGC-Flüge" wird kleiner (compact:true) dargestellt.
                  const smFlights = (loggerDist?.buckets ?? [])
                    .filter(b => b.manufacturer.toLowerCase().includes('sm-modellbau') ||
                                 b.manufacturer.toLowerCase().includes('sm modellbau'))
                    .reduce((sum, b) => sum + b.flights, 0)
                  const smShare = loggerDist && loggerDist.totalRelevantFlights > 0
                    ? (smFlights / loggerDist.totalRelevantFlights) * 100
                    : 0
                  const otherShare = loggerDist && loggerDist.totalRelevantFlights > 0
                    ? 100 - smShare
                    : 0
                  const currentYear = new Date().getFullYear()
                  const tiles: Array<{ value: number; label: string; suffix?: string; decimals?: number; compact?: boolean; tip?: string }> = [
                    // Piloten-Split: Flatland + Slope statt „alle 2026"
                    { value: community?.flatlandPilots ?? 0, label: t('home.statPilotsFlatlandYear', { year: currentYear }), decimals: 0, tip: t('home.statPilotsFlatlandYearTip', { year: currentYear }) },
                    { value: community?.slopePilots ?? 0, label: t('home.statPilotsSlopeYear', { year: currentYear }), decimals: 0, tip: t('home.statPilotsSlopeYearTip', { year: currentYear }) },
                    { value: (platformStats as any)?.registeredPilots ?? (platformStats as any)?.totalPilots ?? 0, label: t('home.statRegisteredPilots'), decimals: 0, tip: t('home.statRegisteredPilotsTip') },
                    // r19.55.72 (Stephan-Spec 2026-07-14): "Piloten Online"-Kachel raus -
                    // ist jetzt prominent im Header oben rechts sichtbar.
                    { value: community?.flatlandKm ?? 0, label: t('home.statKmFlatland'), decimals: 0, tip: t('home.statKmFlatlandTip', { year: currentYear }) },
                    { value: community?.slopeKm ?? 0, label: t('home.statKmSlope'), decimals: 0, tip: t('home.statKmSlopeTip', { year: currentYear }) },
                    { value: Math.round(platformStats?.totalDistanceKm ?? 0), label: t('home.statKmTotal'), decimals: 0, tip: t('home.statKmTotalTip') },
                    { value: community?.yearEarthRounds ?? 0, label: t('home.statEarthRounds'), decimals: 2, tip: t('home.statEarthRoundsTip', { year: currentYear }) },
                    // r19.55.40: IGC-Flüge kleiner dargestellt (compact:true)
                    { value: platformStats?.totalFlights ?? 0, label: t('home.statIgcFlights'), decimals: 0, compact: true, tip: t('home.statIgcFlightsTip') },
                    { value: (platformStats as any)?.totalFlightHours ?? 0, label: t('home.statFlightHours'), suffix: ' h', decimals: 0, tip: t('home.statFlightHoursTip') },
                    // r19.55.40: Logger-Kachel — SM-Modellbau-Anteil (was wertungsfähig bleibt)
                    { value: smShare, label: t('home.statLoggerSmModellbau'), suffix: ' %', decimals: 0, tip: t('home.statLoggerSmModellbauTip') },
                    { value: otherShare, label: t('home.statLoggerOther'), suffix: ' %', decimals: 0, tip: t('home.statLoggerOtherTip') },
                    // Plattform-Stats — Hotspots / Hersteller / Modelle / Sinkpolaren / Kreispolaren
                    { value: platformStats?.thermalHotspots ?? hotspotCount, label: t('home.statHotspots'), decimals: 0, tip: t('home.statHotspotsTip') },
                    { value: modelStats.manufacturers, label: t('home.statManufacturers'), decimals: 0, tip: t('home.statManufacturersTip') },
                    { value: platformStats?.totalModels ?? 0, label: t('home.statModels'), decimals: 0, tip: t('home.statModelsTip') },
                    // r19.55.68/70 (Stephan-Spec 2026-07-14): Deutsche Labels +
                    // Tooltips die erklaeren was den Piloten zur Verfuegung steht.
                    {
                      value: platformStats?.sinkPolars ?? platformStats?.totalPolars ?? 0,
                      label: t('home.statSinkPolarsLabel'),
                      decimals: 0,
                      tip: t('home.statSinkPolarsTip'),
                    },
                    {
                      value: platformStats?.sink5Polars ?? 0,
                      label: t('home.statSink5Polars'),
                      decimals: 0,
                      tip: t('home.statSink5PolarsTip'),
                    },
                    {
                      value: platformStats?.circlePolars ?? 0,
                      label: t('home.statCirclePolarsLabel'),
                      decimals: 0,
                      tip: t('home.statCirclePolarsTip'),
                    },
                    {
                      value: platformStats?.polarContributions ?? 0,
                      label: t('home.statPolarContributions'),
                      decimals: 0,
                      tip: t('home.statPolarContributionsTip'),
                    },
                  ]
                  return tiles.map((s: any) => (
                    <div key={s.label} data-tip={s.tip}>
                      <div
                        className="award-stat-value"
                        style={{
                          fontSize: s.compact
                            ? 'clamp(16px, 1.6vw, 24px)'
                            : 'clamp(22px, 2.4vw, 36px)',
                        }}
                      >
                        <CountUp value={s.value} suffix={s.suffix ?? ''} decimals={s.decimals ?? 0} />
                      </div>
                      <div className="award-stat-label">{s.label}</div>
                    </div>
                  ))
                })()}
              </div>

              {/* Nationen-Flaggen-Zeile — sortiert nach Pilot-Count desc */}
              {community?.nationList && community.nationList.length > 0 && (
                <div className="mt-6 pt-5 border-t border-[var(--award-line)]">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="award-label">{t('home.nationsPilots', { year: new Date().getFullYear() })}</span>
                    <span className="h-px flex-1 bg-[var(--award-line)]" />
                  </div>
                  {/* r19.55.71 (Stephan-Spec 2026-07-14): Fahnen-Zeile auf grossen
                      Monitoren einzeilig gross. Auf mobilen Screens automatisch
                      kleiner (Fahnen + Zahl responsive scaliert), wrappt erst
                      wenn wirklich noetig. Skalierung via clamp(). */}
                  <div className="flex flex-wrap lg:flex-nowrap gap-x-4 gap-y-3 items-center lg:justify-start lg:overflow-x-auto lg:whitespace-nowrap">
                    {community.nationList
                      .slice()
                      .sort((a, b) => b.pilots - a.pilots)
                      .map((n) => (
                        <div key={n.iso} className="inline-flex items-center gap-1.5 shrink-0" data-tip={t('home.flagTooltip', { iso: n.iso, count: n.pilots, countLabel: t(n.pilots === 1 ? 'home.flagPilotsSingular' : 'home.flagPilotsPlural') })}>
                          <img
                            src={`https://flagcdn.com/48x36/${n.iso.toLowerCase()}.png`}
                            srcSet={`https://flagcdn.com/96x72/${n.iso.toLowerCase()}.png 2x`}
                            alt={n.iso}
                            className="rounded-[3px] shadow-sm"
                            style={{ height: 'clamp(20px, 2.4vw, 34px)', width: 'auto' }}
                            width={48}
                            height={36}
                            loading="lazy"
                          />
                          <span className="font-mono tabular-nums" style={{ color: 'var(--award-cirrus-2)', fontSize: 'clamp(14px, 1.6vw, 22px)' }}>
                            <strong style={{ color: 'var(--award-gold)' }}>{n.pilots}</strong>
                          </span>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </Reveal>
          </div>
        </section>

        {/* ═══ LOGGER-TRANSPARENZ ═══
            r19.49 (2026-06-29, Stephan-Spec): welche Logger werden tatsächlich
            in der FGL geflogen? Plus Workaround-Quote (Anteil Eagle/Eagle 2
            mit Portal-Umweg) als sichtbares Druckmittel auf RC-Electronics,
            einen direkten Daten-Export zu öffnen. */}
        {loggerDist && loggerDist.totalRelevantFlights > 0 && (
          <section className="award-section relative" style={{ paddingBlock: 'clamp(36px, 5vw, 72px)' }}>
            <div className="max-w-[1400px] mx-auto px-6 md:px-12">
              <Reveal>
                {/* r19.55.179 (Stephan-Spec 2026-08-10): Ganzer Logger-Block
                    jetzt als natives <details>-Accordion (default zu). Trigger
                    zeigt Live-Stat + Vor-Ankündigungs-Warnung als Info-Chip. */}
                <details className="fgl-accordion-logger group" open={false}>
                  <summary
                    className="cursor-pointer list-none select-none"
                    data-tip={t('home.loggerAccordionTip')}
                  >
                    <div
                      className="award-chroma p-4 md:p-5 flex items-center justify-between gap-4 transition-transform group-hover:-translate-y-0.5"
                      style={{ borderRadius: 16 }}
                    >
                      <div className="flex items-center gap-4 min-w-0">
                        <div
                          className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
                          style={{
                            background: 'color-mix(in oklab, var(--accent) 18%, transparent)',
                            border: '1px solid color-mix(in oklab, var(--accent) 45%, transparent)',
                          }}
                          aria-hidden
                        >
                          <span className="text-lg">🔧</span>
                        </div>
                        <div className="min-w-0 flex-1">
                          <div
                            className="text-[10px] font-black uppercase tracking-[0.18em]"
                            style={{ color: 'var(--accent)' }}
                          >
                            {t('home.loggerTileEyebrow')}
                          </div>
                          <div
                            className="text-[15px] md:text-[17px] font-bold truncate"
                            style={{ color: 'var(--text)', fontFamily: 'var(--font-display)' }}
                          >
                            {t('home.loggerAccordionTitle')}
                          </div>
                          <div
                            className="text-[11px] mt-0.5 flex items-center gap-3 flex-wrap"
                            style={{ color: 'var(--award-cirrus-2)' }}
                          >
                            <span data-tip={t('home.loggerAccordionStatTip')}>
                              📊 {loggerDist.totalRelevantFlights.toLocaleString('de-DE')} {t('home.loggerAccordionStatFlights')}
                            </span>
                            <span className="opacity-60">·</span>
                            <span
                              className="text-orange-400 font-semibold"
                              data-tip={t('home.loggerAccordionWarnTip')}
                            >
                              ⚠ {t('home.loggerAccordionWarnLabel')}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div
                        className="shrink-0 text-[13px] font-black uppercase tracking-[0.15em] flex items-center gap-2"
                        style={{ color: 'var(--accent)' }}
                      >
                        <span className="hidden sm:inline">{t('home.loggerAccordionOpenLabel')}</span>
                        <span
                          className="transition-transform duration-200 group-open:rotate-180"
                          aria-hidden
                        >
                          ▾
                        </span>
                      </div>
                    </div>
                  </summary>
                <div
                  className="award-chroma p-6 md:p-10 mt-3"
                  style={{ borderRadius: 24 }}
                >
                  <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
                    <div>
                      <div className="award-eyebrow mb-2">{t('home.loggerTileEyebrow')}</div>
                      <h2 className="award-h-md" style={{ marginBottom: 6 }}>
                        {t('home.loggerTileTitle')}
                      </h2>
                      <p className="text-[13px] leading-[1.55] max-w-[640px]" style={{ color: 'var(--award-cirrus-2)' }}>
                        {t('home.loggerTileDescription')}
                      </p>
                    </div>
                    {/* r19.55.44 (2026-07-13, Stephan-Direktive): Workaround-
                        Quote-Kachel entfernt — die Information ist jetzt im
                        vertikalen Chart (Anteil-Balken pro Loggertyp inkl.
                        Legende „direkt auslesbar" vs. „Workaround") sichtbar. */}
                  </div>

                  {/* r19.55.43 (2026-07-13, Stephan-Wunsch): Vertikaler Chart
                      der Loggertyp-Anteile aktuell in Verwendung. Bar-Height ∝
                      Prozentanteil an totalRelevantFlights. Farbe: amber für
                      Workaround-Logger, gold/accent für direkt auslesbare. */}
                  {loggerDist.buckets.length > 0 && loggerDist.totalRelevantFlights > 0 && (
                    <div
                      className="mb-6 p-5 rounded-[14px]"
                      style={{
                        background: 'color-mix(in oklab, var(--accent) 4%, transparent)',
                        border: '1px solid color-mix(in oklab, var(--accent) 15%, transparent)',
                      }}
                    >
                      <div
                        className="text-[10px] uppercase tracking-[.14em] font-black mb-3"
                        style={{ color: 'var(--accent)' }}
                      >
                        Anteil der Loggertypen in Verwendung
                      </div>
                      {(() => {
                        const buckets = [...loggerDist.buckets].sort((a, b) => b.flights - a.flights)
                        const maxPct = Math.max(
                          ...buckets.map(b => (b.flights / loggerDist.totalRelevantFlights) * 100),
                          1,
                        )
                        const chartHeight = 160
                        return (
                          <div className="flex items-end justify-between gap-2 md:gap-4" style={{ minHeight: chartHeight + 60 }}>
                            {buckets.map(b => {
                              const pct = (b.flights / loggerDist.totalRelevantFlights) * 100
                              const barH = Math.max(4, (pct / maxPct) * chartHeight)
                              const color = b.requiresWorkaround ? '#f59e0b' : 'var(--accent)'
                              return (
                                <div
                                  key={b.code}
                                  className="flex-1 flex flex-col items-center min-w-0"
                                  data-tip={`${b.manufacturer} ${b.model}: ${b.flights} Flüge (${pct.toFixed(1)} %)`}
                                >
                                  {/* Prozent-Label über dem Balken */}
                                  <div
                                    className="font-mono tabular-nums font-black mb-1"
                                    style={{ fontSize: 11, color: 'var(--text)' }}
                                  >
                                    {pct.toFixed(0)}%
                                  </div>
                                  {/* Vertikaler Balken */}
                                  <div
                                    className="w-full rounded-t-md transition-all"
                                    style={{
                                      height: barH,
                                      background: `linear-gradient(180deg, ${color}, color-mix(in oklab, ${color} 60%, transparent))`,
                                      border: `1px solid ${color}`,
                                      borderBottom: 'none',
                                      maxWidth: 72,
                                      marginInline: 'auto',
                                    }}
                                    aria-label={`${b.manufacturer} ${b.model}: ${pct.toFixed(0)} Prozent`}
                                  />
                                  {/* Grundlinie */}
                                  <div
                                    className="w-full"
                                    style={{
                                      height: 2,
                                      background: 'color-mix(in oklab, var(--text) 25%, transparent)',
                                      maxWidth: 72,
                                      marginInline: 'auto',
                                    }}
                                  />
                                  {/* Beschriftung */}
                                  <div className="mt-2 text-center w-full">
                                    <div
                                      className="text-[8px] uppercase tracking-[.08em] font-black opacity-70 truncate"
                                      style={{ color: 'var(--text)' }}
                                    >
                                      {b.manufacturer}
                                    </div>
                                    <div
                                      className="text-[10px] font-black truncate"
                                      style={{ color: b.requiresWorkaround ? '#f59e0b' : 'var(--text)' }}
                                    >
                                      {b.model}
                                      {b.requiresWorkaround && <span aria-hidden="true" style={{ marginLeft: 3 }}>⚠</span>}
                                    </div>
                                    <div
                                      className="text-[9px] opacity-60 font-mono tabular-nums"
                                      style={{ color: 'var(--text)' }}
                                    >
                                      {b.flights} Flüge
                                    </div>
                                  </div>
                                </div>
                              )
                            })}
                          </div>
                        )
                      })()}
                      {/* Legende unter dem Chart */}
                      <div className="mt-4 pt-3 flex flex-wrap items-center gap-4 text-[10px]" style={{ borderTop: '1px solid color-mix(in oklab, var(--text) 12%, transparent)', color: 'var(--award-cirrus-2)' }}>
                        <div className="flex items-center gap-1.5">
                          <span
                            aria-hidden="true"
                            style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: 'var(--accent)' }}
                          />
                          <span>Direkt auslesbar (IGC vom Logger)</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span
                            aria-hidden="true"
                            style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: '#f59e0b' }}
                          />
                          <span>⚠ Workaround erforderlich (Portal-Umweg)</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Bucket-Liste — sortiert nach Flügen desc */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {loggerDist.buckets.map((b) => {
                      const pct = loggerDist.totalRelevantFlights > 0
                        ? (b.flights / loggerDist.totalRelevantFlights) * 100
                        : 0
                      // r19.49: Trend-Pfeil — interpretiert sich pro Logger
                      // unterschiedlich. Für Workaround-Logger ist ↑ "schlecht"
                      // (mehr Pilots im Portal-Umweg gefangen), für direkten
                      // Logger ist ↑ "gut". Visuell: ↑ amber, ↓ grün, → grau.
                      const trendIcon =
                        b.trendDirection === 'up' ? '↑' :
                        b.trendDirection === 'down' ? '↓' : '→'
                      const trendColor =
                        b.trendDirection === 'flat' ? 'var(--award-cirrus-3)' :
                        b.requiresWorkaround
                          ? (b.trendDirection === 'up' ? '#f59e0b' : '#34d399')
                          : (b.trendDirection === 'up' ? '#34d399' : '#f59e0b')
                      const trendTip =
                        b.recentFlights === 0 && b.previousFlights === 0
                          ? t('home.loggerTileNoTrendData')
                          : t('home.loggerTileTrendTip', {
                              recent: b.recentFlights,
                              previous: b.previousFlights,
                              sign: b.trendDeltaShare >= 0 ? '+' : '',
                              deltaShare: b.trendDeltaShare.toFixed(1),
                            })
                      const flightLabel = t(b.flights === 1 ? 'home.loggerTileFlightSingular' : 'home.loggerTileFlightPlural')
                      return (
                        <div
                          key={b.code}
                          className="relative p-4 rounded-[14px] overflow-hidden"
                          style={{
                            background: b.requiresWorkaround
                              ? 'rgba(245, 158, 11, 0.08)'
                              : 'color-mix(in oklab, var(--accent) 8%, transparent)',
                            border: `1px solid ${b.requiresWorkaround ? 'rgba(245, 158, 11, 0.35)' : 'color-mix(in oklab, var(--accent) 25%, transparent)'}`,
                          }}
                          data-tip={b.requiresWorkaround
                            ? t('home.loggerTileBucketWorkaroundTip', {
                                manufacturer: b.manufacturer,
                                model: b.model,
                                flights: b.flights,
                                flightLabel,
                                trendTip,
                              })
                            : t('home.loggerTileBucketDirectTip', {
                                manufacturer: b.manufacturer,
                                model: b.model,
                                flights: b.flights,
                                flightLabel,
                                trendTip,
                              })}
                        >
                          {/* Fortschrittsbalken im Hintergrund */}
                          <div
                            aria-hidden="true"
                            style={{
                              position: 'absolute',
                              left: 0,
                              top: 0,
                              bottom: 0,
                              width: `${pct}%`,
                              background: b.requiresWorkaround
                                ? 'rgba(245, 158, 11, 0.10)'
                                : 'color-mix(in oklab, var(--accent) 10%, transparent)',
                              zIndex: 0,
                            }}
                          />
                          <div className="relative flex items-center justify-between gap-3" style={{ zIndex: 1 }}>
                            <div className="min-w-0 flex-1">
                              <div className="text-[10px] uppercase tracking-[.12em] font-black opacity-70" style={{ color: 'var(--text)' }}>
                                {b.manufacturer}
                              </div>
                              <div className="text-[14px] font-black truncate" style={{ color: b.requiresWorkaround ? '#f59e0b' : 'var(--text)' }}>
                                {b.model}
                                {b.requiresWorkaround && <span style={{ marginLeft: 6, fontSize: 12 }}>⚠</span>}
                              </div>
                            </div>
                            {/* r19.55.41 (2026-07-13, Stephan-Wunsch):
                                Trend-Pfeil raus, Anzahl-Flüge bleibt, dahinter
                                ein Donut-Chart mit dem Nutzungs-Prozent. */}
                            <div className="flex items-center gap-3">
                              <div
                                className="font-mono tabular-nums font-black text-right"
                                style={{ color: 'var(--text)', minWidth: 40 }}
                              >
                                <span style={{ fontSize: 18, lineHeight: 1 }}>{b.flights}</span>
                              </div>
                              {(() => {
                                // Donut-Kreis 36px mit Rand-Segment = Nutzungsanteil
                                const size = 36
                                const strokeW = 4
                                const r = (size - strokeW) / 2
                                const cx = size / 2
                                const cy = size / 2
                                const circumference = 2 * Math.PI * r
                                const usage = Math.max(0, Math.min(100, pct))
                                const dash = (usage / 100) * circumference
                                const gap = circumference - dash
                                const ringColor = b.requiresWorkaround
                                  ? '#f59e0b'
                                  : 'var(--accent)'
                                return (
                                  <div
                                    className="relative shrink-0"
                                    style={{ width: size, height: size }}
                                    aria-label={`${pct.toFixed(0)}% Anteil`}
                                  >
                                    <svg
                                      width={size}
                                      height={size}
                                      viewBox={`0 0 ${size} ${size}`}
                                      style={{ display: 'block' }}
                                    >
                                      {/* Hintergrund-Ring */}
                                      <circle
                                        cx={cx}
                                        cy={cy}
                                        r={r}
                                        fill="none"
                                        stroke="currentColor"
                                        strokeOpacity="0.15"
                                        strokeWidth={strokeW}
                                      />
                                      {/* Fortschritts-Segment */}
                                      <circle
                                        cx={cx}
                                        cy={cy}
                                        r={r}
                                        fill="none"
                                        stroke={ringColor}
                                        strokeWidth={strokeW}
                                        strokeLinecap="round"
                                        strokeDasharray={`${dash} ${gap}`}
                                        transform={`rotate(-90 ${cx} ${cy})`}
                                      />
                                    </svg>
                                    <div
                                      className="absolute inset-0 flex items-center justify-center font-mono tabular-nums font-black"
                                      style={{ fontSize: 10, color: 'var(--text)' }}
                                    >
                                      {pct.toFixed(0)}%
                                    </div>
                                  </div>
                                )
                              })()}
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>

                  {/* Strategischer Quote-Block — sichtbar bei Workaround > 0 */}
                  {loggerDist.workaroundFlights > 0 && (
                    <div
                      className="mt-6 p-5 rounded-[14px]"
                      style={{
                        background: 'color-mix(in oklab, var(--accent) 6%, transparent)',
                        border: '1px solid color-mix(in oklab, var(--accent) 20%, transparent)',
                      }}
                    >
                      <div className="award-label mb-2">{t('home.loggerTilePositionLabel')}</div>
                      <p className="text-[13px] leading-[1.6]" style={{ color: 'var(--text)' }}>
                        {t('home.loggerTilePositionText')}
                      </p>
                    </div>
                  )}

                  {/* r19.55.41 (2026-07-13, Stephan-Direktive): Roter Warnblock
                      zur angekuendigten RC-Electronics CSV-Umstellung. Info-
                      Quelle: Vorstand des GPS-Triangle (schriftlich vorliegend),
                      noch NICHT vom Hersteller offiziell bestaetigt. Wird als
                      Vor-Ankuendigung an Piloten kommuniziert, damit sie beim
                      naechsten Logger-Kauf beim Hersteller nachfragen koennen
                      ob IGC-Files erzeugt werden. */}
                  {/* r19.55.182 (Stephan-Spec 2026-08-10): Warn-Block-Text
                      jetzt via i18n (dangerouslySetInnerHTML für <strong> + <a>).
                      Vorher war der ganze Block hardcoded DE inline. */}
                  <div
                    className="mt-4 p-5 rounded-[14px]"
                    style={{
                      background: 'rgba(239, 68, 68, 0.08)',
                      border: '1px solid rgba(239, 68, 68, 0.35)',
                    }}
                    role="alert"
                    aria-live="polite"
                  >
                    <div
                      className="award-label mb-2 flex items-center gap-1.5"
                      style={{ color: '#dc2626' }}
                    >
                      <span aria-hidden="true">⚠</span>
                      <span>{t('home.loggerWarnTitle')}</span>
                    </div>
                    <p
                      className="text-[13px] leading-[1.6]"
                      style={{ color: '#7f1d1d' }}
                      dangerouslySetInnerHTML={{ __html: t('home.loggerWarnBody') }}
                    />
                  </div>
                </div>
                </details>
              </Reveal>
            </div>
          </section>
        )}

        {/* ═══ BENTO-GRID — Was die Plattform kann ═══
            Kompakter Abstand 2026-05-09: award-section default ist 80-180px,
            wir override auf py-12 (mobil) / py-20 (desktop) damit der Sprung
            vom Hero zur Bento nicht zu groß wirkt. */}
        <section className="award-section relative" style={{ paddingBlock: 'clamp(48px, 6vw, 96px)' }}>
          <div className="max-w-[1400px] mx-auto px-6 md:px-12">
            <Reveal>
              <div className="flex items-end justify-between flex-wrap gap-4 mb-16">
                <div>
                  <div className="award-eyebrow mb-3">{t('home.bentoEyebrow')}</div>
                  <h2 className="award-h-lg">
                    {t('home.bentoTitle1')}<br />
                    <span className="award-grad-text">{t('home.bentoTitle2')}</span>
                  </h2>
                </div>
                <p className="max-w-[460px] text-[15px] leading-[1.6]" style={{ color: 'var(--award-cirrus-2)' }}>
                  {t('home.bentoLead')}
                </p>
              </div>
            </Reveal>

            {/* r19.55.179 (Stephan-Spec 2026-08-10): Bento-Grid komplett neu.
                6 Karten mit Live-Stats + Schwarmwissen-Framing + Tooltipps.
                Jede Card hat eine Live-Zahl aus platformStats/community/modelStats.
                Alle Texte via t() für spätere Übersetzung. */}
            <div className="grid grid-cols-12 gap-4 md:gap-5">

              {/* 01 — Thermik-Atlas (Featured, 8/12) */}
              <Reveal delay={1} className="col-span-12 md:col-span-8 award-chroma p-8 md:p-10 relative overflow-hidden min-h-[320px]">
                <div className="relative z-10">
                  <div className="award-chip mb-5" data-tip={t('home.swarmThermalTip')}>
                    {t('home.swarmThermalChip')}
                  </div>
                  <h3 className="award-h-md mb-4">{t('home.swarmThermalTitle')}</h3>
                  <p className="text-[15px] max-w-[560px] leading-[1.6]" style={{ color: 'var(--award-cirrus-2)' }}>
                    {t('home.swarmThermalBody')}
                  </p>
                  <div className="mt-6 flex items-baseline gap-3" data-tip={t('home.swarmThermalStatTip')}>
                    <span className="award-stat-value" style={{ fontSize: '42px', color: 'var(--award-gold)' }}>
                      <CountUp value={platformStats?.thermalHotspots ?? 0} />
                    </span>
                    <span className="award-label">{t('home.swarmThermalStatLabel')}</span>
                  </div>
                </div>
                <div className="award-thermal-dot" style={{ right: '14%', top: '30%' }} />
                <div className="award-thermal-dot" style={{ right: '22%', top: '58%', animationDelay: '1.2s' }} />
                <div className="award-thermal-dot" style={{ right: '40%', top: '44%', animationDelay: '2.6s' }} />
                <div className="absolute -right-8 -bottom-8 w-[280px] h-[280px] rounded-full opacity-20"
                  style={{ background: 'radial-gradient(circle, var(--award-gold), transparent 70%)' }} />
              </Reveal>

              {/* 02 — Polaren aus dem Schwarm (4/12) */}
              <Reveal delay={2} className="col-span-12 md:col-span-4 award-glass p-8 min-h-[320px] flex flex-col">
                <div
                  className="award-chip mb-5"
                  style={{ background: 'rgba(30,77,92,0.18)', color: 'var(--award-petrol-hi)', borderColor: 'rgba(42,110,130,0.32)' }}
                  data-tip={t('home.swarmPolarTip')}
                >
                  {t('home.swarmPolarChip')}
                </div>
                <h3 className="award-h-md mb-4">{t('home.swarmPolarTitle1')}<br />{t('home.swarmPolarTitle2')}</h3>
                <p className="text-[14px] leading-[1.55]" style={{ color: 'var(--award-cirrus-2)' }}>
                  {t('home.swarmPolarBody')}
                </p>
                <div className="mt-auto pt-6 border-t border-[var(--award-line)]">
                  <div className="flex justify-between items-baseline" data-tip={t('home.swarmPolarStatTip')}>
                    <span className="award-label">{t('home.swarmPolarStatLabel')}</span>
                    <span className="award-stat-value" style={{ fontSize: '32px' }}>
                      <CountUp value={modelStats.polars || 0} />
                    </span>
                  </div>
                </div>
              </Reveal>

              {/* 03 — FGL-Wertung mit echten Regeln (4/12) */}
              <Reveal delay={1} className="col-span-12 md:col-span-4 award-glass p-7 min-h-[240px] flex flex-col justify-between">
                <div>
                  <div className="award-chip mb-4" data-tip={t('home.swarmRankingTip')}>
                    {t('home.swarmRankingChip')}
                  </div>
                  <h3 className="award-h-md" style={{ fontSize: '22px' }}>
                    {t('home.swarmRankingTitle1')}<br />
                    <span className="award-grad-text">{t('home.swarmRankingTitle2')}</span>
                  </h3>
                  <p className="text-[13px] mt-3" style={{ color: 'var(--award-cirrus-2)' }}>
                    {t('home.swarmRankingBody')}
                  </p>
                </div>
                <div className="mt-4 flex items-baseline justify-between" data-tip={t('home.swarmRankingStatTip')}>
                  <span className="award-label">{t('home.swarmRankingStatLabel')}</span>
                  <span className="award-stat-value" style={{ fontSize: '28px' }}>
                    <CountUp value={community?.yearTotalFlights ?? 0} />
                  </span>
                </div>
              </Reveal>

              {/* 04 — KI-Coaching auf eigener Hardware (4/12) */}
              <Reveal delay={2} className="col-span-12 md:col-span-4 award-glass p-7 min-h-[240px] flex flex-col justify-between">
                <div>
                  <div className="award-chip mb-4" data-tip={t('home.swarmAiTip')}>
                    {t('home.swarmAiChip')}
                  </div>
                  <h3 className="award-h-md" style={{ fontSize: '22px' }}>
                    {t('home.swarmAiTitle1')}<br />
                    <span className="award-grad-text">{t('home.swarmAiTitle2')}</span>
                  </h3>
                  <p className="text-[13px] mt-3" style={{ color: 'var(--award-cirrus-2)' }}>
                    {t('home.swarmAiBody')}
                  </p>
                </div>
                <div
                  className="mt-4 inline-flex items-center gap-2 text-[12px] font-black uppercase tracking-widest"
                  style={{ color: 'var(--award-gold)' }}
                  data-tip={t('home.swarmAiStatTip')}
                >
                  <span className="w-2 h-2 rounded-full bg-[var(--award-gold)] animate-pulse" />
                  {t('home.swarmAiStatLabel')}
                </div>
              </Reveal>

              {/* 05 — Cesium-Globus mit eigenem 3D-Terrain (4/12) */}
              <Reveal delay={3} className="col-span-12 md:col-span-4 award-glass p-7 min-h-[240px] flex flex-col justify-between">
                <div>
                  <div className="award-chip mb-4" data-tip={t('home.swarmTerrainTip')}>
                    {t('home.swarmTerrainChip')}
                  </div>
                  <h3 className="award-h-md" style={{ fontSize: '22px' }}>
                    {t('home.swarmTerrainTitle1')}<br />
                    <span className="award-grad-text">{t('home.swarmTerrainTitle2')}</span>
                  </h3>
                  <p className="text-[13px] mt-3" style={{ color: 'var(--award-cirrus-2)' }}>
                    {t('home.swarmTerrainBody')}
                  </p>
                </div>
                <div
                  className="mt-4 text-[12px] font-black uppercase tracking-widest"
                  style={{ color: 'var(--award-cirrus-2)' }}
                  data-tip={t('home.swarmTerrainStatTip')}
                >
                  {t('home.swarmTerrainStatLabel')}
                </div>
              </Reveal>

              {/* 06 — NEU: Akku-Ladebuch mit Setup-Kopplung (12/12, full-width) */}
              <Reveal delay={4} className="col-span-12 award-chroma p-8 md:p-10 relative overflow-hidden min-h-[220px]">
                <div className="relative z-10 grid md:grid-cols-[1fr_auto] gap-6 items-center">
                  <div>
                    <div
                      className="award-chip mb-4"
                      style={{ background: 'rgba(16,185,129,0.16)', color: '#10b981', borderColor: 'rgba(16,185,129,0.42)' }}
                      data-tip={t('home.swarmBatteryTip')}
                    >
                      {t('home.swarmBatteryChip')}
                    </div>
                    <h3 className="award-h-md mb-3">
                      {t('home.swarmBatteryTitle1')} <span className="award-grad-text">{t('home.swarmBatteryTitle2')}</span>
                    </h3>
                    <p className="text-[14px] max-w-[720px] leading-[1.6]" style={{ color: 'var(--award-cirrus-2)' }}>
                      {t('home.swarmBatteryBody')}
                    </p>
                  </div>
                  <div className="flex flex-col items-start md:items-end gap-2">
                    <Link
                      href="/features"
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-[12px] font-black uppercase tracking-widest transition-transform hover:-translate-y-0.5"
                      style={{
                        background: 'linear-gradient(180deg, #10b981 0%, #059669 100%)',
                        color: '#ffffff',
                        boxShadow: '0 4px 14px -4px rgba(16,185,129,0.55)',
                      }}
                      data-tip={t('home.swarmBatteryCtaTip')}
                    >
                      {t('home.swarmBatteryCta')} →
                    </Link>
                    <span className="text-[11px]" style={{ color: 'var(--award-cirrus-3)' }}>
                      {t('home.swarmBatteryBadge')}
                    </span>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </section>

        {/* ═══ LIVE-TICKER — Editorial Marquee ═══ */}
        {fglWertungActive && tickerAll.length > 0 && (
          <section className="relative py-20 md:py-28 border-y border-[var(--award-line)] overflow-hidden">
            <div className="max-w-[1400px] mx-auto px-6 md:px-12 mb-10">
              <Reveal>
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div>
                    <div className="award-eyebrow mb-2">
                      <span className="inline-flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-[var(--award-ember)] animate-pulse" />
                        {t('home.tickerLive')}
                      </span>
                    </div>
                    <h2 className="award-h-lg">{t('home.tickerTitle')}</h2>
                  </div>
                  <Link href="/wertung" className="award-link text-[13px]">{t('home.tickerSeeAll')} →</Link>
                </div>
              </Reveal>
            </div>

            <div className="relative">
              <div className="absolute inset-y-0 left-0 w-32 z-10 pointer-events-none"
                style={{ background: 'linear-gradient(90deg, var(--award-ink) 0%, transparent 100%)' }} />
              <div className="absolute inset-y-0 right-0 w-32 z-10 pointer-events-none"
                style={{ background: 'linear-gradient(-90deg, var(--award-ink) 0%, transparent 100%)' }} />
              <div className="award-marquee">
                {[...tickerAll, ...tickerAll].map((f, i) => (
                  <div key={f.flight_guid + i}
                    className="flex items-center gap-4 px-5 py-3 rounded-full border"
                    style={{ borderColor: 'var(--award-line)', background: 'var(--award-ink-2)' }}>
                    <span className={`award-label ${f.terrain === 'slope' ? 'text-[var(--award-gold)]' : ''}`}>
                      {f.terrain === 'slope' ? 'SLOPE' : 'FLAT'}
                    </span>
                    <span className="text-[14px] font-bold whitespace-nowrap" style={{ fontFamily: 'var(--font-display)' }}>
                      {f.pilot_name}
                    </span>
                    {flagImg(f.country)}
                    <span className="font-mono text-[13px] text-[var(--award-gold)] whitespace-nowrap">
                      {fmtKm(f.score_km)} km
                    </span>
                    {f.avg_speed_kmh && f.avg_speed_kmh >= 70 && (
                      <span className="font-mono text-[11px]" style={{ color: 'var(--award-cirrus-3)' }}>
                        {f.avg_speed_kmh.toFixed(0)} km/h
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* r19.55.179 (Stephan-Spec 2026-08-10): Sektion 5 (Login-CTA) und
            Sektion 6 (Closing-CTA) zusammengelegt als „So läuft der Flug"-
            Zeitleiste mit 6 Schritten. Ein dominanter CTA am Ende. */}
        {false && rankings.length > 0 && fglWertungVisibility !== 'hidden' && null}

        <section className="relative py-16 md:py-24 overflow-hidden border-t border-[var(--award-line)]">
          <div className="absolute inset-0 opacity-[0.5] pointer-events-none">
            <div
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[900px] rounded-full"
              style={{ background: 'radial-gradient(circle, rgba(242,201,78,0.14), transparent 65%)' }}
            />
          </div>
          <div className="relative z-10 max-w-[1200px] mx-auto px-6 md:px-12">
            <Reveal>
              <div className="text-center mb-12 md:mb-16">
                <div className="award-eyebrow mb-4">{t('home.journeyEyebrow')}</div>
                <h2 className="award-h-lg" style={{ fontSize: 'clamp(30px, 4.4vw, 60px)' }}>
                  {t('home.journeyTitle1')} <span className="award-grad-text">{t('home.journeyTitle2')}</span>
                </h2>
                <p className="award-body-lead mx-auto mt-5" style={{ maxWidth: '58ch' }}>
                  {t('home.journeyLead')}
                </p>
              </div>
            </Reveal>

            {/* 6-Schritte-Zeitleiste — desktop horizontal, mobile vertikal */}
            <Reveal delay={1}>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5 mb-10">
                {([
                  { n: '1', keyBase: 'journey1', tip: 'journey1Tip' },
                  { n: '2', keyBase: 'journey2', tip: 'journey2Tip' },
                  { n: '3', keyBase: 'journey3', tip: 'journey3Tip' },
                  { n: '4', keyBase: 'journey4', tip: 'journey4Tip' },
                  { n: '5', keyBase: 'journey5', tip: 'journey5Tip' },
                  { n: '6', keyBase: 'journey6', tip: 'journey6Tip' },
                ] as const).map((step) => (
                  <div
                    key={step.n}
                    data-fgl-3d="true"
                    className="award-glass p-6 md:p-7 flex flex-col gap-3 transition-transform hover:-translate-y-1"
                    data-tip={t(`home.${step.tip}`)}
                    style={{ minHeight: 170 }}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className="w-10 h-10 rounded-full flex items-center justify-center text-[15px] font-black"
                        style={{
                          background: 'color-mix(in oklab, var(--accent) 22%, transparent)',
                          border: '1px solid color-mix(in oklab, var(--accent) 55%, transparent)',
                          color: 'var(--accent)',
                          fontFamily: 'var(--font-display)',
                        }}
                        aria-hidden
                      >
                        {step.n}
                      </div>
                      <h3
                        className="font-black uppercase tracking-[0.14em] text-[13px] md:text-[14px]"
                        style={{ color: 'var(--text)' }}
                      >
                        {t(`home.${step.keyBase}Title`)}
                      </h3>
                    </div>
                    <p className="text-[13px] leading-[1.55]" style={{ color: 'var(--award-cirrus-2)' }}>
                      {t(`home.${step.keyBase}Body`)}
                    </p>
                  </div>
                ))}
              </div>
            </Reveal>

            {/* Ein dominanter CTA */}
            <Reveal delay={2}>
              <div className="text-center mt-14 md:mt-16">
                <Magnetic>
                  <Link
                    href="/login"
                    className="inline-flex items-center gap-3 px-8 py-4 md:px-10 md:py-5 rounded-full font-black uppercase tracking-[0.16em] transition-transform hover:-translate-y-1"
                    style={{
                      background: 'linear-gradient(180deg, var(--award-gold) 0%, #d19d1f 100%)',
                      color: '#0a0a0a',
                      fontSize: 'clamp(14px, 1.6vw, 18px)',
                      fontFamily: 'var(--font-display)',
                      boxShadow: '0 10px 32px -8px rgba(242,201,78,0.55)',
                    }}
                    data-tip={t('home.journeyCtaPrimaryTip')}
                  >
                    {t('home.journeyCtaPrimary')}
                    <span aria-hidden>→</span>
                  </Link>
                </Magnetic>
                <div className="mt-4">
                  <Link
                    href="/features"
                    className="text-[13px] font-bold tracking-[0.12em] uppercase inline-flex items-center gap-1.5 hover:gap-2.5 transition-all"
                    style={{ color: 'var(--award-cirrus-2)' }}
                    data-tip={t('home.journeyCtaSecondaryTip')}
                  >
                    {t('home.journeyCtaSecondary')} →
                  </Link>
                </div>
                <p className="mt-6 text-[11px]" style={{ color: 'var(--award-cirrus-3)' }}>
                  {t('home.journeyFootnote')}
                </p>
                <p className="mt-2 text-[11px]" style={{ color: 'var(--award-cirrus-3)' }}>
                  {t('home.closingProject')}
                </p>
              </div>
            </Reveal>
          </div>
        </section>
      </div>
    </LenisProvider>
  )
}

/**
 * r19.45 (2026-06-26): Frontpage-Hero mit 2 Race-Tracks (Flatland + Slope).
 * Eingeloggte werden im jeweils richtigen Streifen mit gelbem Pulse markiert.
 * r19.45.1: Pillen-Switch oben — Overall / Bester Einzelflug / Club / Standard / Open.
 */
function FrontpageRaceHero() {
  const { t } = useTranslation()
  const { data: session } = useSession()
  const myUserId = (session?.user as { id?: string } | undefined)?.id ?? null
  const [view, setView] = useState<RaceTrackView>('overall')

  // r19.45.4 (Stephan-Spec 2026-06-26): Tab-Label vs. Header-Label trennen,
  // damit die Pillen kompakt bleiben und der Header die echte Wertungs-
  // Kategorie zeigt ('Klassenwertung Open' statt nur 'Open').
  const VIEWS: Array<{ key: RaceTrackView; tabLabel: string; headerLabel: string }> = [
    { key: 'overall',     tabLabel: t('wertung.viewOverall'),         headerLabel: t('wertung.viewOverall') },
    { key: 'einzelflug',  tabLabel: t('home.raceTrackBestFlightTab'), headerLabel: t('wertung.viewSingleBest') },
    { key: 'CLUBCLASS',   tabLabel: t('wertung.classClub'),           headerLabel: t('wertung.classRankingClub') },
    { key: 'STANDARD',    tabLabel: t('wertung.classStandard'),       headerLabel: t('wertung.classRankingStandard') },
    { key: 'OPEN',        tabLabel: t('wertung.classOpen'),           headerLabel: t('wertung.classRankingOpen') },
  ]

  const viewLabel = VIEWS.find(v => v.key === view)?.headerLabel ?? t('wertung.viewOverall')

  return (
    <section className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 mt-6 mb-4">
      <div
        className="rounded-3xl p-5 md:p-7"
        style={{
          background: 'color-mix(in oklch, var(--bg-card) 70%, transparent)',
          border: '1px solid var(--border)',
          backdropFilter: 'blur(8px)',
        }}
      >
        {/* View-Switch Pillen + Zur-Wertung-CTA */}
        <div className="flex items-center justify-between gap-3 flex-wrap mb-5">
          <div className="flex items-center gap-1.5 flex-wrap">
            {VIEWS.map(v => {
              const active = view === v.key
              return (
                <button
                  key={v.key}
                  onClick={() => setView(v.key)}
                  data-state={active ? 'active' : 'inactive'}
                  className="wertung-tab-pill px-3 py-1.5 rounded-full text-[11px] font-black uppercase tracking-wider"
                >
                  {v.tabLabel}
                </button>
              )
            })}
          </div>
          {/* r19.45.3 (Stephan-Spec 2026-06-26): grosse CTA-Pille rechts oben.
              r19.49 (Stephan-Spec 2026-06-29): respektiert den aktiven View —
              Default Flatland, eingeloggte Slope-Piloten ggf. später. */}
          <Link
            href={buildWertungLink('FLATLAND', view)}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-[12px] font-black uppercase tracking-widest transition-all hover:scale-105"
            style={{
              background: 'linear-gradient(135deg, var(--accent) 0%, #c79935 100%)',
              color: '#0a0e16',
              boxShadow: '0 8px 24px rgba(242,201,78,.35), 0 0 0 1px rgba(255,255,255,.15) inset',
            }}
          >
            <Trophy className="w-4 h-4" />
            {t('home.raceTrackCtaRanking')}
          </Link>
        </div>
        <div className="space-y-7">
          <FglRaceTrack
            terrain="FLATLAND"
            label={t('home.raceTrackFlatlandLabel', { view: viewLabel, year: 2026 })}
            showLiveDot
            highlightUserId={myUserId}
            height={70}
            view={view}
            topN={30}
          />
          <FglRaceTrack
            terrain="SLOPE"
            label={t('home.raceTrackSlopeLabel', { view: viewLabel, year: 2026 })}
            highlightUserId={myUserId}
            height={70}
            view={view}
            topN={30}
          />
        </div>
      </div>
    </section>
  )
}
